import { Button } from "@/components/ui/button";
import { editProjectschema, HandleSaveEditValues, Project } from "@/api/studio/projects/types/project.type";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useUpdateProject } from "@/api/studio/projects/queries";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { VisibilityToggle } from "../components/visibility-toggle";
import { useModalError } from "@/hooks/use-modal-error";

/* Prop Types */
interface EditProjectModalProps {
  project: Project;
}

/*
 * EditProjectModal component
 * This component is used to edit a projects name and visibility (private or public)
 */
export const EditProjectModal = ({ project }: EditProjectModalProps) => {
  /* Queries */
  const { mutateAsync: updateProject, isPending: isUpdateProjectPending, isError: isUpdateProjectError } = useUpdateProject();

  /* Hooks */
  const { close } = useModalError({ error: isUpdateProjectError });

  /* Handlers */
  const handleSave = async (values: HandleSaveEditValues) => {
    // if value.visibility is true, set it to "public", otherwise set it to "private"
    // this was done to match the schema of the project object and keep the usage of checkboxes consistent
    const updatedValues = { ...values };
    updatedValues.visibility = values.visibility ? "private" : "public";
    await updateProject({
      id: project.id,
      values: {
        name: updatedValues.name,
        visibility: updatedValues.visibility,
        thumbnailName: project.thumbnailName,
      },
    });
    close();
  };

  /* Render */
  return (
    <Form onSubmit={handleSave} schema={editProjectschema} options={{ defaultValues: { name: project.name } }}>
      {({ register, formState: { errors } }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2>Edit project</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <TextInput
            label="Project title"
            placeholder="For example 'Promotion Campaign Spring 2025 Studies'"
            error={errors.name?.message}
            registration={register("name")}
            className="mb-4"
          />

          {/* Visibility toggle */}
          <VisibilityToggle checked={project.visibility === "private" ? true : false} register={register} />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="primary" disabled={isUpdateProjectPending}>
              {isUpdateProjectPending ? "Saving..." : "Continue"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
